import type React from "react";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Building2, ChevronDown, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@nexus/ui";
import type { NorthStarKpi } from "@nexus/contracts";
import { sortBrandsByVolumeDesc } from "@nexus/dashboard-kpis/competitor-filter-defaults";
import type { AdPlatformOption, BrandOption, IndustryOption, ProductOption } from "@/features/opportunities/api/opportunity-sets.api";
import { AdPlatformIcon } from "@/features/opportunities/components/AdPlatformIcon.js";
import type { DashboardMode, CompetitorFilters, MyBrandFilters } from "../../../types/dashboard.types";
import type { ExploreDateSelectionFields } from "../../../types/dashboard.types";
import { ExploreDateRangePicker } from "../ExploreDateRangePicker/ExploreDateRangePicker";
import { findBrandImageUrl } from "../../../lib/competitor-filter-defaults";
import { formatBrandName } from "../../../lib/format.js";
import { platformDisplayName } from "../../../lib/my-brand-filter-options";
import { AnimatedHeight } from "../AnimatedHeight/AnimatedHeight.js";
import { BrandMark } from "../../BrandMark/BrandMark";
import { ExploreApplyControls } from "./ExploreApplyControls.js";
import { BrandVolumeCaption } from "./BrandVolumeCaption.js";
import {
  FILTER_TRIGGER_BASE,
  FILTER_TRIGGER_INVALID,
  FILTER_TRIGGER_LABEL,
  FILTER_TRIGGER_VALUE,
} from "../../../lib/filter-trigger-styles.js";
import type { ExploreApplyDisableReason } from "../../../lib/can-apply-explore-filters";

interface GlobalFilterHeaderProps {
  mode: DashboardMode;
  onModeChange: (mode: DashboardMode) => void;
  /** When false, hide the My Brand / Competitors toggle. Mode is locked by the caller. */
  showModeToggle?: boolean;
  // Apply guardrail: filter edits only fire the KPI request once applied.
  isDirty: boolean;
  canApply: boolean;
  applyDisabledReason?: ExploreApplyDisableReason;
  onApply: () => void;
  onDiscard: () => void;
  /**
   * The active mode's date selection. Its three fields move together, so they
   * are set through one callback rather than the per-key filter setters.
   */
  onDateSelectionChange: (selection: ExploreDateSelectionFields) => void;
  // Competitors
  competitorFilters: CompetitorFilters;
  onCompetitorFilterChange: <K extends keyof CompetitorFilters>(key: K, value: CompetitorFilters[K]) => void;
  industries: IndustryOption[];
  industriesLoading: boolean;
  competitorDefaultsLoading?: boolean;
  myIndustryBrands: BrandOption[];
  myIndustryBrandsLoading: boolean;
  competitorBrands: BrandOption[];
  competitorBrandsLoading: boolean;
  competitorBrandsError?: boolean;
  // My Brand
  myBrandFilters: MyBrandFilters;
  onMyBrandFilterChange: <K extends keyof MyBrandFilters>(key: K, value: MyBrandFilters[K]) => void;
  accounts: { id: string; name: string }[];
  recentAccounts: { id: string; name: string }[];
  onAccountRecorded: (accountId: string) => void;
  recentProducts: { id: string; name: string }[];
  onProductRecorded: (productId: string) => void;
  adPlatforms: AdPlatformOption[];
  adPlatformsLoading: boolean;
  competitorPlatforms: AdPlatformOption[];
  competitorPlatformsLoading: boolean;
  competitorPlatformsError: boolean;
  competitorPlatformsDisabled: boolean;
  // Shared
  products: ProductOption[];
  productsLoading: boolean;
  productsToCompare: ProductOption[];
  productsToCompareLoading: boolean;
  northStar: NorthStarKpi;
  onNorthStarChange: (kpi: NorthStarKpi) => void;
  /** Route-specific page filters (Overview / Drilldown) rendered under the global row. */
  pageFilters?: ReactNode;
}

const NORTH_STAR_KPI_OPTIONS: { value: NorthStarKpi; label: string }[] = [
  { value: "roas", label: "ROAS" },
  { value: "cpa", label: "CPA" },
  { value: "custom", label: "Custom KPI" },
];

function brandMarkKind(name: string, myBrand: string | null): "you" | "competitor" {
  return myBrand !== null && name === myBrand ? "you" : "competitor";
}

function BrandNameWithMark({
  name,
  imageUrl,
  kind,
  className,
}: {
  name: string;
  imageUrl?: string | null;
  kind: "you" | "competitor";
  className?: string;
}) {
  return (
    <span className={cn("flex min-w-0 items-center gap-1.5", className)}>
      <BrandMark label={name} logoUrl={imageUrl} kind={kind} />
      <span className="truncate">{formatBrandName(name)}</span>
    </span>
  );
}

/**
 * Radix ItemText shrink-wraps its contents, so justify-between never sees the
 * menu width. Stretch that span to the item.
 */
const BRAND_OPTION_ITEM_CLASS =
  "[&>span:last-child]:flex [&>span:last-child]:w-full [&>span:last-child]:min-w-0 [&>span:last-child]:flex-1";

/** Room under the last option so the select scroll-down control does not cover it. */
const BRAND_SELECT_MENU_CLASS = "[&_[data-radix-select-viewport]]:pb-6";

function PillSelectTrigger({
  label,
  className,
  children,
  loading,
  status,
  hasValue = true,
}: {
  label: string;
  className?: string;
  children?: ReactNode;
  loading?: boolean;
  status?: "error" | "warning";
  hasValue?: boolean;
}) {
  const activeStatus = loading ? undefined : status;
  const isInvalid = activeStatus === "error" || activeStatus === "warning";
  return (
    <SelectTrigger
      className={cn(
        FILTER_TRIGGER_BASE,
        "w-auto",
        loading && "animate-pulse pointer-events-none",
        isInvalid && FILTER_TRIGGER_INVALID,
        className,
      )}
    >
      <span className="inline-flex items-center gap-1">
        {hasValue && (
          <span className={FILTER_TRIGGER_LABEL}>{label}</span>
        )}
        {children ?? <SelectValue />}
      </span>
    </SelectTrigger>
  );
}

function MultiSelectPopoverTrigger({
  label,
  selected,
  disabled,
  loading,
  error,
  formatItem = formatBrandName,
  maxShown = 3,
  className,
  total,
  ...rest
}: {
  label: string;
  selected: string[];
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  formatItem?: (s: string) => string;
  maxShown?: number;
  total?: number;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type">) {
  const showAll =
    total !== undefined
      ? total > 0 && selected.length >= total
      : selected.length === 0;
  const showNone = !showAll && selected.length === 0;
  const showError = error && !loading && !disabled;
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={cn(
        "inline-flex min-w-0 items-center gap-1.5",
        FILTER_TRIGGER_BASE,
        disabled
          ? "cursor-not-allowed border-border bg-muted/40 text-muted-foreground opacity-50 hover:bg-muted/40"
          : showError && FILTER_TRIGGER_INVALID,
        loading && !disabled && "animate-pulse pointer-events-none",
        className,
      )}
      {...rest}
    >
      <span className={FILTER_TRIGGER_LABEL}>{label}</span>
      {loading && !disabled ? (
        <span className="shrink-0 text-muted-foreground">Loading…</span>
      ) : showNone ? (
        <span className="shrink-0 text-muted-foreground">None</span>
      ) : showAll ? (
        <span className={FILTER_TRIGGER_VALUE}>All</span>
      ) : (
        <>
          <span className={cn("min-w-0 truncate", FILTER_TRIGGER_VALUE)}>
            {selected.slice(0, maxShown).map(formatItem).join(", ")}
          </span>
          {selected.length > maxShown && (
            <span className={FILTER_TRIGGER_VALUE}>+{selected.length - maxShown}</span>
          )}
        </>
      )}
      <ChevronDown className="h-3 w-3 shrink-0 opacity-50" aria-hidden />
    </button>
  );
}

export function GlobalFilterHeader({
  mode,
  onModeChange,
  showModeToggle = true,
  isDirty,
  canApply,
  applyDisabledReason,
  onApply,
  onDiscard,
  competitorFilters,
  onCompetitorFilterChange,
  onDateSelectionChange,
  industries,
  industriesLoading,
  competitorDefaultsLoading = false,
  myIndustryBrands,
  myIndustryBrandsLoading,
  competitorBrands,
  competitorBrandsLoading,
  competitorBrandsError = false,
  myBrandFilters,
  onMyBrandFilterChange,
  accounts,
  recentAccounts,
  onAccountRecorded,
  recentProducts,
  onProductRecorded,
  adPlatforms,
  adPlatformsLoading,
  competitorPlatforms,
  competitorPlatformsLoading,
  competitorPlatformsError,
  competitorPlatformsDisabled,
  products,
  productsLoading,
  productsToCompare,
  productsToCompareLoading,
  northStar,
  onNorthStarChange,
  pageFilters,
}: GlobalFilterHeaderProps) {
  const { t } = useTranslation();
  const [productSearch, setProductSearch] = useState("");
  const [benchmarkSearch, setBenchmarkSearch] = useState("");
  const [myBrandProductSearch, setMyBrandProductSearch] = useState("");
  const [accountSearch, setAccountSearch] = useState("");
  // Recent ids selected in the open popover, flushed to the recents list on close.
  const [pendingRecentAccounts, setPendingRecentAccounts] = useState<string[]>([]);
  const [pendingRecentProducts, setPendingRecentProducts] = useState<string[]>([]);

  const competitorsDisabled = competitorFilters.competitorsIndustry === null;
  const myBrandDisabled = competitorFilters.myIndustry === null;

  const industryOptionsLoading = industriesLoading || competitorDefaultsLoading;

  // Each industry dropdown tracks its own empty state independently
  const myIndustryError = !industryOptionsLoading && competitorFilters.myIndustry === null;
  const competitorsIndustryError = !industryOptionsLoading && competitorFilters.competitorsIndustry === null;

  const handleProductToggle = (productId: string) => {
    const current = competitorFilters.industryProducts;
    const next = current.includes(productId)
      ? current.filter((p) => p !== productId)
      : [...current, productId];
    onCompetitorFilterChange("industryProducts", next);
  };

  const handleMyBrandProductToggle = (productId: string) => {
    const current = myBrandFilters.products ?? products.map((p) => p.id);
    const next = current.includes(productId)
      ? current.filter((p) => p !== productId)
      : [...current, productId];
    onMyBrandFilterChange("products", next.length >= products.length ? null : next);
  };

  const handleMyBrandPlatformToggle = (platformId: string) => {
    // Local var so TypeScript can narrow null away before the array operations.
    const platforms = myBrandFilters.platforms;
    const current = platforms === null ? adPlatforms.map((p) => p.id) : platforms;
    const next = current.includes(platformId)
      ? current.filter((p) => p !== platformId)
      : [...current, platformId];
    onMyBrandFilterChange(
      "platforms",
      adPlatforms.length > 0 && next.length >= adPlatforms.length ? null : next,
    );
  };

  const handleMyBrandAccountToggle = (accountId: string) => {
    const selectedAccounts = myBrandFilters.accounts;
    const current = selectedAccounts === null ? accounts.map((a) => a.id) : selectedAccounts;
    const next = current.includes(accountId)
      ? current.filter((id) => id !== accountId)
      : [...current, accountId];
    onMyBrandFilterChange(
      "accounts",
      accounts.length > 0 && next.length >= accounts.length ? null : next,
    );
  };

  const handleBenchmarkToggle = (brandName: string) => {
    const current = competitorFilters.benchmark;
    const next = current.includes(brandName)
      ? current.filter((b) => b !== brandName)
      : [...current, brandName];
    onCompetitorFilterChange("benchmark", next);
  };

  const competitorPickerBrands = useMemo(
    () => sortBrandsByVolumeDesc(competitorBrands),
    [competitorBrands],
  );

  const productsToCompareByVolume = useMemo(
    () => sortBrandsByVolumeDesc(productsToCompare),
    [productsToCompare],
  );

  const filteredBenchmarkBrands = competitorPickerBrands.filter((brand) =>
    formatBrandName(brand.name).toLowerCase().includes(benchmarkSearch.toLowerCase()),
  );

  const platformLabel = (value: string) =>
    competitorPlatforms.find((p) => p.id === value)?.name ?? platformDisplayName(t, value);

  const handlePlatformToggle = (value: string) => {
    const current = competitorFilters.platforms;
    const next = current.includes(value)
      ? current.filter((p) => p !== value)
      : [...current, value];
    onCompetitorFilterChange("platforms", next);
  };

  const activeDateFilters = mode === "competitors" ? competitorFilters : myBrandFilters;

  const myBrandWarning = !myBrandDisabled && competitorFilters.myBrand === null;
  const mainCompetitorWarning = !competitorsDisabled && competitorFilters.mainCompetitor === null;

  return (
    <div
      className="sticky top-0 z-10 flex shrink-0 flex-col border-b border-border bg-background"
      data-dashboard-filter-bar
    >

      {/* ── Row 1: controls ── */}
      <AnimatedHeight>
      <div className="flex min-h-[54px] flex-wrap items-center gap-x-3 gap-y-2 px-5 py-2">

        {/* Mode toggle */}
        {showModeToggle && (
          <>
            <ToggleGroup
              type="single"
              variant="pill"
              value={mode}
              onValueChange={(v) => v && onModeChange(v as DashboardMode)}
              aria-label={t("dashboard.globalFilterHeader.exploreModeAria", "explore mode")}
              className="shrink-0"
            >
              <ToggleGroupItem
                value="my-brand"
                size="sm"
                aria-label={t("dashboard.globalFilterHeader.myBrand", "My Brand")}
                className="gap-1.5"
              >
                <Building2 className="size-3.5" aria-hidden />
                {t("dashboard.globalFilterHeader.myBrand", "My Brand")}
              </ToggleGroupItem>
              <ToggleGroupItem
                value="competitors"
                size="sm"
                aria-label={t("dashboard.globalFilterHeader.competitors", "Competitors")}
                className="gap-1.5"
              >
                <Users className="size-3.5" aria-hidden />
                {t("dashboard.globalFilterHeader.competitors", "Competitors")}
              </ToggleGroupItem>
            </ToggleGroup>

            <Separator orientation="vertical" className="h-6 shrink-0" />
          </>
        )}

        {/* ── Competitors filters ── */}
        {mode === "competitors" && (
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">

            {/* Platforms */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <MultiSelectPopoverTrigger
                        label={t("dashboard.globalFilterHeader.platformsLabel", "Platforms:")}
                        selected={competitorFilters.platforms}
                        total={competitorPlatforms.length}
                        formatItem={platformLabel}
                        maxShown={2}
                        loading={competitorPlatformsLoading && !competitorPlatformsDisabled}
                        disabled={competitorPlatformsDisabled}
                        error={competitorPlatformsError}
                        aria-label={
                          competitorPlatformsLoading
                            ? t("dashboard.globalFilterHeader.platformsLoadingAria", "Loading platforms")
                            : undefined
                        }
                      />
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-44 p-1">
                      {competitorPlatformsDisabled ? (
                        <p className="px-3 py-2 text-xs text-muted-foreground">
                          {t(
                            "dashboard.globalFilterHeader.platformsSelectIndustryFirst",
                            "Select competitors industry first",
                          )}
                        </p>
                      ) : competitorPlatformsError ? (
                        <p className="px-3 py-2 text-xs text-muted-foreground">
                          {t(
                            "dashboard.globalFilterHeader.platformsError",
                            "Failed to load platforms",
                          )}
                        </p>
                      ) : competitorPlatforms.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-muted-foreground">
                          {t("dashboard.globalFilterHeader.platformsEmpty", "No platforms found")}
                        </p>
                      ) : (
                        competitorPlatforms.map((opt) => (
                          <label
                            key={opt.id}
                            className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-xs hover:bg-accent"
                          >
                            <Checkbox
                              checked={competitorFilters.platforms.includes(opt.id)}
                              onCheckedChange={() => handlePlatformToggle(opt.id)}
                              className="h-3.5 w-3.5"
                            />
                            <AdPlatformIcon platformId={opt.id} />
                            <span>{opt.name}</span>
                          </label>
                        ))
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
              </TooltipTrigger>
              {competitorPlatformsDisabled && (
                <TooltipContent>
                  {t(
                    "dashboard.globalFilterHeader.platformsSelectIndustryFirst",
                    "Select competitors industry first",
                  )}
                </TooltipContent>
              )}
            </Tooltip>

            {/* My Industry */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Select
                    value={competitorFilters.myIndustry ?? ""}
                    onValueChange={(v) => onCompetitorFilterChange("myIndustry", v || null)}
                    disabled={industryOptionsLoading}
                  >
                    <PillSelectTrigger
                      label="My Industry:"
                      loading={industryOptionsLoading}
                      status={myIndustryError ? "error" : undefined}
                      hasValue={!!competitorFilters.myIndustry}
                    >
                      <SelectValue placeholder="My industry is required" />
                    </PillSelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind.index} value={ind.index}>
                          {ind.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TooltipTrigger>
              {myIndustryError && <TooltipContent>Select industry</TooltipContent>}
            </Tooltip>

            {/* My Brand */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Select
                    value={competitorFilters.myBrand ?? ""}
                    onValueChange={(v) => onCompetitorFilterChange("myBrand", v || null)}
                    disabled={myBrandDisabled || myIndustryBrandsLoading}
                  >
                    <PillSelectTrigger
                      label="My Brand:"
                      loading={myIndustryBrandsLoading && !myBrandDisabled}
                      status={myBrandWarning ? "warning" : undefined}
                      className={myBrandDisabled ? "cursor-not-allowed opacity-50" : undefined}
                      hasValue={!!competitorFilters.myBrand}
                    >
                      {competitorFilters.myBrand ? (
                        <BrandNameWithMark
                          name={competitorFilters.myBrand}
                          imageUrl={findBrandImageUrl(myIndustryBrands, competitorFilters.myBrand)}
                          kind="you"
                        />
                      ) : (
                        <span className="truncate">My brand is required</span>
                      )}
                    </PillSelectTrigger>
                    <SelectContent className={BRAND_SELECT_MENU_CLASS}>
                      {myIndustryBrands.map((b) => (
                        <SelectItem key={b.name} value={b.name}>
                          <BrandNameWithMark name={b.name} imageUrl={b.imageUrl} kind="you" />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TooltipTrigger>
              {myBrandWarning && <TooltipContent>Crucial info missing</TooltipContent>}
            </Tooltip>

            {/* Products to compare (from my industry) */}
            <Popover onOpenChange={(open) => { if (!open) setProductSearch(""); }}>
              <PopoverTrigger asChild>
                <MultiSelectPopoverTrigger
                  label="Products to compare:"
                  selected={competitorFilters.industryProducts}
                  total={productsToCompare.length}
                  loading={productsToCompareLoading}
                  formatItem={(id) => formatBrandName(productsToCompare.find((p) => p.id === id)?.name ?? id)}
                  maxShown={1}
                  className="max-w-52"
                />
              </PopoverTrigger>
              <PopoverContent align="start" className="w-56 p-1">
                {!competitorFilters.myIndustry ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">Select my industry first</p>
                ) : productsToCompare.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">No products found</p>
                ) : (
                  <>
                    <div className="p-1 pb-0">
                      <input
                        type="text"
                        placeholder="Search products…"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        className="w-full rounded-md border border-input bg-transparent px-2 py-1.5 text-xs outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
                      />
                    </div>
                    {productsToCompareByVolume.filter((p) =>
                      formatBrandName(p.name).toLowerCase().includes(productSearch.toLowerCase()),
                    ).length === 0 ? (
                      <p className="px-3 py-2 text-xs text-muted-foreground">No results for "{productSearch}"</p>
                    ) : (
                      <div className="max-h-56 overflow-y-auto pt-1">
                        {!productSearch && (
                          <>
                            <label className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-xs font-medium hover:bg-accent">
                              <Checkbox
                                checked={competitorFilters.industryProducts.length >= productsToCompare.length}
                                onCheckedChange={(checked) =>
                                  onCompetitorFilterChange(
                                    "industryProducts",
                                    checked ? productsToCompare.map((p) => p.id) : [],
                                  )
                                }
                                className="h-3.5 w-3.5"
                              />
                              <span>All</span>
                            </label>
                            <div className="mx-3 mb-1 border-t border-border/50" />
                          </>
                        )}
                        {productsToCompareByVolume
                          .filter((p) => formatBrandName(p.name).toLowerCase().includes(productSearch.toLowerCase()))
                          .map((p) => (
                            <label
                              key={p.id}
                              className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-xs hover:bg-accent"
                            >
                              <Checkbox
                                checked={competitorFilters.industryProducts.includes(p.id)}
                                onCheckedChange={() => handleProductToggle(p.id)}
                                className="h-3.5 w-3.5"
                              />
                              <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                                <span className="truncate">{formatBrandName(p.name)}</span>
                                <BrandVolumeCaption count={p.assetCount90d} />
                              </span>
                            </label>
                          ))}
                      </div>
                    )}
                  </>
                )}
              </PopoverContent>
            </Popover>

            <div aria-hidden className="h-6 w-px shrink-0 bg-border" />

            {/* Competitors Industry */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Select
                    value={competitorFilters.competitorsIndustry ?? ""}
                    onValueChange={(v) => onCompetitorFilterChange("competitorsIndustry", v || null)}
                    disabled={industryOptionsLoading}
                  >
                    <PillSelectTrigger
                      label="Competitors Industry:"
                      loading={industryOptionsLoading}
                      status={competitorsIndustryError ? "error" : undefined}
                      hasValue={!!competitorFilters.competitorsIndustry}
                    >
                      <SelectValue placeholder="Competitor industry is required" />
                    </PillSelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind.index} value={ind.index}>
                          {ind.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TooltipTrigger>
              {competitorsIndustryError && <TooltipContent>Select industry</TooltipContent>}
            </Tooltip>

            {/* Main Competitor */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Select
                    value={competitorFilters.mainCompetitor ?? ""}
                    onValueChange={(v) => onCompetitorFilterChange("mainCompetitor", v || null)}
                    disabled={competitorsDisabled || competitorBrandsLoading}
                  >
                    <PillSelectTrigger
                      label="Main Competitor:"
                      loading={competitorBrandsLoading && !competitorsDisabled}
                      status={
                        competitorBrandsError
                          ? "error"
                          : mainCompetitorWarning
                            ? "warning"
                            : undefined
                      }
                      className={competitorsDisabled ? "cursor-not-allowed opacity-50" : undefined}
                      hasValue={!!competitorFilters.mainCompetitor}
                    >
                      {competitorFilters.mainCompetitor ? (
                        <BrandNameWithMark
                          name={competitorFilters.mainCompetitor}
                          imageUrl={findBrandImageUrl(
                            competitorPickerBrands,
                            competitorFilters.mainCompetitor,
                          )}
                          kind={brandMarkKind(
                            competitorFilters.mainCompetitor,
                            competitorFilters.myBrand,
                          )}
                        />
                      ) : (
                        <span className="truncate">Main competitor is required</span>
                      )}
                    </PillSelectTrigger>
                    <SelectContent className={cn("min-w-72", BRAND_SELECT_MENU_CLASS)}>
                      {competitorBrandsError ? (
                        <p className="px-3 py-2 text-xs text-muted-foreground">
                          {t(
                            "dashboard.globalFilterHeader.mainCompetitorError",
                            "Failed to load competitors",
                          )}
                        </p>
                      ) : !competitorBrandsLoading && competitorPickerBrands.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-muted-foreground">
                          {t(
                            "dashboard.globalFilterHeader.mainCompetitorEmpty",
                            "No brands on the selected platforms",
                          )}
                        </p>
                      ) : (
                        competitorPickerBrands.map((b) => (
                          <SelectItem key={b.name} value={b.name} className={BRAND_OPTION_ITEM_CLASS}>
                            <span className="flex w-full min-w-0 items-center gap-2">
                              <BrandNameWithMark
                                name={b.name}
                                imageUrl={b.imageUrl}
                                kind={brandMarkKind(b.name, competitorFilters.myBrand)}
                                className="min-w-0 flex-1"
                              />
                              <BrandVolumeCaption count={b.assetCount90d} />
                            </span>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </TooltipTrigger>
              {mainCompetitorWarning && <TooltipContent>Crucial info missing</TooltipContent>}
            </Tooltip>

            {/* Benchmark (competitors from competitors industry) */}
            <Popover onOpenChange={(open) => { if (!open) setBenchmarkSearch(""); }}>
              <PopoverTrigger asChild>
                <MultiSelectPopoverTrigger
                  label={t("dashboard.globalFilterHeader.benchmarkLabel", "Benchmark:")}
                  selected={competitorFilters.benchmark}
                  total={competitorPickerBrands.length}
                  loading={competitorBrandsLoading && !competitorsDisabled}
                  disabled={competitorsDisabled}
                  error={competitorBrandsError}
                  formatItem={formatBrandName}
                  maxShown={1}
                  className="max-w-52"
                />
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[28rem] p-1">
                {competitorsDisabled ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    {t(
                      "dashboard.globalFilterHeader.benchmarkSelectIndustryFirst",
                      "Select competitors industry first",
                    )}
                  </p>
                ) : competitorBrandsError ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    {t(
                      "dashboard.globalFilterHeader.benchmarkError",
                      "Failed to load competitors",
                    )}
                  </p>
                ) : competitorPickerBrands.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    {t(
                      "dashboard.globalFilterHeader.mainCompetitorEmpty",
                      "No brands on the selected platforms",
                    )}
                  </p>
                ) : (
                  <>
                    <div className="p-1 pb-0">
                      <input
                        type="text"
                        placeholder={t(
                          "dashboard.globalFilterHeader.benchmarkSearchPlaceholder",
                          "Search competitors…",
                        )}
                        value={benchmarkSearch}
                        onChange={(e) => setBenchmarkSearch(e.target.value)}
                        className="w-full rounded-md border border-input bg-transparent px-2 py-1.5 text-xs outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
                      />
                    </div>
                    {filteredBenchmarkBrands.length === 0 ? (
                      <p className="px-3 py-2 text-xs text-muted-foreground">
                        {t(
                          "dashboard.globalFilterHeader.benchmarkNoResults",
                          'No results for "{{query}}"',
                          { query: benchmarkSearch },
                        )}
                      </p>
                    ) : (
                      <div className="max-h-56 overflow-y-auto pt-1">
                        {!benchmarkSearch && (
                          <>
                            <label className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-xs font-medium hover:bg-accent">
                              <Checkbox
                                checked={
                                  competitorPickerBrands.length > 0 &&
                                  competitorFilters.benchmark.length >= competitorPickerBrands.length
                                }
                                onCheckedChange={(checked) =>
                                  onCompetitorFilterChange(
                                    "benchmark",
                                    checked ? competitorPickerBrands.map((b) => b.name) : [],
                                  )
                                }
                                className="h-3.5 w-3.5"
                              />
                              <span>{t("dashboard.globalFilterHeader.benchmarkAll", "All")}</span>
                            </label>
                            <div className="mx-3 mb-1 border-t border-border/50" />
                          </>
                        )}
                        {filteredBenchmarkBrands.map((brand) => {
                          const isMain = brand.name === competitorFilters.mainCompetitor;
                          const isMyBrand = brand.name === competitorFilters.myBrand;
                          return (
                            <label
                              key={brand.name}
                              className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-xs hover:bg-accent"
                            >
                              <Checkbox
                                checked={competitorFilters.benchmark.includes(brand.name)}
                                onCheckedChange={() => handleBenchmarkToggle(brand.name)}
                                className="h-3.5 w-3.5 shrink-0"
                              />
                              {isMain && (
                                <span className="shrink-0 text-[10px] text-muted-foreground">
                                  {t("dashboard.globalFilterHeader.benchmarkMainBadge", "Main")}
                                </span>
                              )}
                              {isMyBrand && (
                                <span className="shrink-0 text-[10px] text-muted-foreground">
                                  {t("dashboard.globalFilterHeader.benchmarkMyBrandBadge", "My brand")}
                                </span>
                              )}
                              <BrandMark
                                label={brand.name}
                                logoUrl={brand.imageUrl}
                                kind={brandMarkKind(brand.name, competitorFilters.myBrand)}
                              />
                              <span className="min-w-32 flex-1 truncate">
                                {formatBrandName(brand.name)}
                              </span>
                              <BrandVolumeCaption count={brand.assetCount90d} />
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </PopoverContent>
            </Popover>

          </div>
        )}

        {/* ── My Brand filters ── */}
        {mode === "my-brand" && (
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">

            {/* Platforms */}
            <Popover>
              <PopoverTrigger asChild>
                <MultiSelectPopoverTrigger
                  label="Platforms:"
                  selected={myBrandFilters.platforms ?? []}
                  total={myBrandFilters.platforms === null ? undefined : adPlatforms.length}
                  formatItem={(id) => adPlatforms.find((p) => p.id === id)?.name ?? id}
                  loading={adPlatformsLoading}
                  // null=All (valid), []=invalid; keep in sync with ExploreFilterHeader validity expression
                  error={myBrandFilters.platforms !== null && myBrandFilters.platforms.length === 0}
                />
              </PopoverTrigger>
              <PopoverContent align="start" className="w-48 p-1">
                {adPlatforms.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    {t("dashboard.globalFilterHeader.platformsEmpty", "No platforms found")}
                  </p>
                ) : (
                  <div className="max-h-56 overflow-y-auto">
                    <label className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-xs font-medium hover:bg-accent">
                      <Checkbox
                        checked={
                          myBrandFilters.platforms === null
                            ? true
                            : myBrandFilters.platforms.length >= adPlatforms.length
                        }
                        onCheckedChange={(checked) => onMyBrandFilterChange("platforms", checked ? null : [])}
                        className="h-3.5 w-3.5"
                      />
                      <span>All</span>
                    </label>
                    <div className="my-1 border-t border-input" />
                    {adPlatforms.map((p) => (
                      <label
                        key={p.id}
                        className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-xs hover:bg-accent"
                      >
                        <Checkbox
                          checked={
                            myBrandFilters.platforms === null
                              ? true
                              : myBrandFilters.platforms.includes(p.id)
                          }
                          onCheckedChange={() => handleMyBrandPlatformToggle(p.id)}
                          className="h-3.5 w-3.5"
                        />
                        <AdPlatformIcon platformId={p.id} />
                        <span>{p.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {/* Accounts */}
            <Popover onOpenChange={(open) => { if (!open) { setAccountSearch(""); pendingRecentAccounts.forEach((id) => onAccountRecorded(id)); setPendingRecentAccounts([]); } }}>
              <PopoverTrigger asChild>
                <MultiSelectPopoverTrigger
                  label="Ad accounts:"
                  selected={myBrandFilters.accounts ?? []}
                  total={myBrandFilters.accounts === null ? undefined : accounts.length}
                  formatItem={(id) => accounts.find((a) => a.id === id)?.name ?? id}
                  maxShown={1}
                  className="max-w-52"
                  error={myBrandFilters.accounts !== null && myBrandFilters.accounts.length === 0}
                />
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-1">
                {accounts.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">No accounts found</p>
                ) : (
                  <>
                    <div className="p-1 pb-0">
                      <input
                        type="text"
                        placeholder="Search accounts…"
                        value={accountSearch}
                        onChange={(e) => setAccountSearch(e.target.value)}
                        className="w-full rounded-md border border-input bg-transparent px-2 py-1.5 text-xs outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
                      />
                    </div>
                    {accounts.filter((a) =>
                      a.name.toLowerCase().includes(accountSearch.toLowerCase()),
                    ).length === 0 ? (
                      <p className="px-3 py-2 text-xs text-muted-foreground">No results for "{accountSearch}"</p>
                    ) : (
                      <div className="max-h-56 overflow-y-auto pt-1">
                        {!accountSearch && (
                          <>
                            <label className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-xs font-medium hover:bg-accent">
                              <Checkbox
                                checked={myBrandFilters.accounts === null}
                                onCheckedChange={(checked) =>
                                  onMyBrandFilterChange("accounts", checked ? null : [])
                                }
                                className="h-3.5 w-3.5"
                              />
                              <span>All</span>
                            </label>
                            <div className="my-1 border-t border-input" />
                            {recentAccounts.length > 0 && (
                              <>
                                <p className="px-3 py-1 text-xs font-medium text-muted-foreground">Recent</p>
                                {recentAccounts.map((a) => (
                                  <label
                                    key={`recent-${a.id}`}
                                    className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-xs hover:bg-accent"
                                  >
                                    <Checkbox
                                      checked={
                                        myBrandFilters.accounts === null
                                          ? true
                                          : myBrandFilters.accounts.includes(a.id)
                                      }
                                      onCheckedChange={() => {
                                        if (!myBrandFilters.accounts?.includes(a.id)) {
                                          setPendingRecentAccounts((prev) =>
                                            prev.includes(a.id) ? prev : [...prev, a.id],
                                          );
                                        }
                                        handleMyBrandAccountToggle(a.id);
                                      }}
                                      className="h-3.5 w-3.5"
                                    />
                                    <span className="truncate">{a.name}</span>
                                  </label>
                                ))}
                                <div className="my-1 border-t border-input" />
                              </>
                            )}
                          </>
                        )}
                        {accounts
                          .filter((a) => a.name.toLowerCase().includes(accountSearch.toLowerCase()))
                          .filter((a) => accountSearch || !recentAccounts.some((r) => r.id === a.id))
                          .map((a) => (
                            <label
                              key={a.id}
                              className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-xs hover:bg-accent"
                            >
                              <Checkbox
                                checked={
                                  myBrandFilters.accounts === null
                                    ? true
                                    : myBrandFilters.accounts.includes(a.id)
                                }
                                onCheckedChange={() => {
                                  if (!myBrandFilters.accounts?.includes(a.id)) {
                                    setPendingRecentAccounts((prev) =>
                                      prev.includes(a.id) ? prev : [...prev, a.id],
                                    );
                                  }
                                  handleMyBrandAccountToggle(a.id);
                                }}
                                className="h-3.5 w-3.5"
                              />
                              <span className="truncate">{a.name}</span>
                            </label>
                          ))}
                      </div>
                    )}
                  </>
                )}
              </PopoverContent>
            </Popover>

            {/* Products */}
            <Popover onOpenChange={(open) => { if (!open) { setMyBrandProductSearch(""); pendingRecentProducts.forEach((id) => onProductRecorded(id)); setPendingRecentProducts([]); } }}>
              <PopoverTrigger asChild>
                <MultiSelectPopoverTrigger
                  label="Products:"
                  selected={myBrandFilters.products ?? []}
                  total={myBrandFilters.products === null ? undefined : products.length}
                  loading={productsLoading}
                  formatItem={(id) => formatBrandName(products.find((p) => p.id === id)?.name ?? id)}
                  maxShown={1}
                  className="max-w-52"
                  // null=All (valid), []=invalid; keep in sync with ExploreFilterHeader validity expression
                  error={myBrandFilters.products !== null && myBrandFilters.products.length === 0 && !productsLoading}
                />
              </PopoverTrigger>
              <PopoverContent align="start" className="w-56 p-1">
                {products.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">No products found</p>
                ) : (
                  <>
                    <div className="p-1 pb-0">
                      <input
                        type="text"
                        placeholder="Search products…"
                        value={myBrandProductSearch}
                        onChange={(e) => setMyBrandProductSearch(e.target.value)}
                        className="w-full rounded-md border border-input bg-transparent px-2 py-1.5 text-xs outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
                      />
                    </div>
                    {products.filter((p) =>
                      formatBrandName(p.name).toLowerCase().includes(myBrandProductSearch.toLowerCase()),
                    ).length === 0 ? (
                      <p className="px-3 py-2 text-xs text-muted-foreground">No results for "{myBrandProductSearch}"</p>
                    ) : (
                      <div className="max-h-56 overflow-y-auto pt-1">
                        {!myBrandProductSearch && (
                          <>
                            <label className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-xs font-medium hover:bg-accent">
                              <Checkbox
                                checked={myBrandFilters.products === null || (products.length > 0 && (myBrandFilters.products?.length ?? 0) >= products.length)}
                                onCheckedChange={(checked) =>
                                  onMyBrandFilterChange("products", checked ? null : [])
                                }
                                className="h-3.5 w-3.5"
                              />
                              <span>All</span>
                            </label>
                            <div className="my-1 border-t border-input" />
                            {recentProducts.length > 0 && (
                              <>
                                <p className="px-3 py-1 text-xs font-medium text-muted-foreground">Recent</p>
                                {recentProducts.map((p) => (
                                  <label
                                    key={`recent-${p.id}`}
                                    className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-xs hover:bg-accent"
                                  >
                                    <Checkbox
                                      checked={myBrandFilters.products === null || myBrandFilters.products.includes(p.id)}
                                      onCheckedChange={() => {
                                        if (myBrandFilters.products !== null && !myBrandFilters.products.includes(p.id)) {
                                          setPendingRecentProducts((prev) => prev.includes(p.id) ? prev : [...prev, p.id]);
                                        }
                                        handleMyBrandProductToggle(p.id);
                                      }}
                                      className="h-3.5 w-3.5"
                                    />
                                    <span className="truncate">{formatBrandName(p.name)}</span>
                                  </label>
                                ))}
                                <div className="my-1 border-t border-input" />
                              </>
                            )}
                          </>
                        )}
                        {products
                          .filter((p) => formatBrandName(p.name).toLowerCase().includes(myBrandProductSearch.toLowerCase()))
                          .filter((p) => myBrandProductSearch || !recentProducts.some((r) => r.id === p.id))
                          .map((p) => (
                            <label
                              key={p.id}
                              className="flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-xs hover:bg-accent"
                            >
                              <Checkbox
                                checked={myBrandFilters.products === null || myBrandFilters.products.includes(p.id)}
                                onCheckedChange={() => {
                                  if (myBrandFilters.products !== null && !myBrandFilters.products.includes(p.id)) {
                                    setPendingRecentProducts((prev) => prev.includes(p.id) ? prev : [...prev, p.id]);
                                  }
                                  handleMyBrandProductToggle(p.id);
                                }}
                                className="h-3.5 w-3.5"
                              />
                              <span className="truncate">{formatBrandName(p.name)}</span>
                            </label>
                          ))}
                      </div>
                    )}
                  </>
                )}
              </PopoverContent>
            </Popover>

            {/* North Star KPI — view-state; applies instantly (no Apply click) */}
            <Select value={northStar} onValueChange={(v) => onNorthStarChange(v as NorthStarKpi)}>
              <PillSelectTrigger label="North Star KPI:" className="max-w-44">
                <SelectValue />
              </PillSelectTrigger>
              <SelectContent>
                {NORTH_STAR_KPI_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Date range */}
        <ExploreDateRangePicker
          startDate={activeDateFilters.startDate}
          endDate={activeDateFilters.endDate}
          activePresetId={activeDateFilters.activePresetId}
          onChange={onDateSelectionChange}
          triggerClassName={cn(FILTER_TRIGGER_BASE, "w-auto shrink-0")}
        />

        {/* Apply guardrail — on dashboard (no page filters) only; overview/drilldown use page row */}
        {pageFilters == null && (
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <ExploreApplyControls
              isDirty={isDirty}
              canApply={canApply}
              disableReason={applyDisabledReason}
              onApply={onApply}
              onDiscard={onDiscard}
            />
          </div>
        )}
      </div>
      </AnimatedHeight>

      {pageFilters != null && pageFilters}

    </div>
  );
}
